import React, { memo, Fragment, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTheme, createUseStyles, Modal, Select, Advice, Text, Loader } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { getList } from 'lib/services/valueLists';

import Filters, { FiltersProvider } from 'components/Filters';
import FilterOff from './icons/FilterOff';

import styles from './styles';
const useStyles = createUseStyles(styles, 'ModalFilters');

function ModalFilters({
    advice,
    adviceType = 'info',
    className,
    title,
    confirmText,
    cancelText,
    backText,
    placeholderSearch,
    isOpen,
    schema,
    selectOptions,
    onConfirm,
    onCancel = () => {},
    onBack,
    size = 'medium',
    confirmIsDisabled,
    overlayClassName,
    postComponent,
    isLoading,
    isReady,
    selectedFilters,
    openFilters,
    filtersValues,
    onChangeSelectedFilters,
    onChangeFilter,
    onToggleFilter,
}) {
    const classes = useStyles();
    const theme = useTheme();
    const { t, language } = useWeb5Context();
    const rootClassNames = classnames(classes.root, className, { [classes.notReady]: !isReady });
    const debounce = useRef(null);
    const cachedOptions = useRef({});

    const loadFuzzyOptions = useCallback(
        (field) => {
            return (text) => {
                return new Promise((resolve) => {
                    if (!field.inputAttrs) return resolve([]);
                    if (cachedOptions.current[text]) return resolve(cachedOptions[text]);
                    clearTimeout(debounce.current);
                    debounce.current = setTimeout(() => {
                        getList({ list: field.inputAttrs.list, language, searchText: text })
                            .then(({ data }) => {
                                let options = data.map((branch) => ({
                                    value: branch.id,
                                    label: branch.description,
                                }));
                                if (field.beforeRenderValueList) {
                                    options = field.beforeRenderValueList(options);
                                }
                                if (field.groupFunction) {
                                    options = field.groupFunction(data);
                                }
                                cachedOptions.current[text] = options;
                                resolve(options);
                            })
                            .catch(console.error);
                    }, 500);
                });
            };
        },
        [language],
    );

    const loadOptions = useCallback(
        (field) => () => {
            if (!field?.inputAttrs?.list) return Promise.resolve([]);
            return getList({ list: field.inputAttrs.list, language }).then(({ data }) =>
                data.map((branch) => ({
                    value: branch.id,
                    label: branch.description,
                })),
            );
        },
        [language],
    );

    const finalSchema = useMemo(() => {
        schema?.map((el) => {
            el.fields.map((field) => {
                if (!field?.inputProps) field.inputProps = {};
                field.inputProps.className = `section-filter__field__${field.id}`;
                field.inputProps.classes = {
                    input: `section-filter__field__${field.id}--input`,
                    clear: `section-filter__field__${field.id}--clear`,
                    calendarIcon: `section-filter__field__${field.id}--calendar`,
                    clockIcon: `section-filter__field__${field.id}--clock`,
                };
                if (field.dataType === 'multipleValueList') {
                    field.inputProps.isMulti = true;
                    if (field?.inputAttrs?.list && field?.inputAttrs?.isFuzzy) {
                        field.inputProps.isFuzzy = true;
                        field.inputProps.loadOptions = loadFuzzyOptions(field);
                        field.inputProps.defaultOptions = true;
                        field.inputProps.defaultSearch = field.inputAttrs?.defaultSearch || '';
                    } else if (field?.inputAttrs?.list) {
                        field.inputProps.loadOptions = loadOptions(field);
                    }
                }

                return field;
            });

            return el;
        });
        return schema;
    }, [loadFuzzyOptions, loadOptions, schema]);

    const selectedFiltesSchema = useMemo(
        () => finalSchema.filter((el) => el.id in filtersValues),
        [finalSchema, filtersValues],
    );

    const preComponent = useMemo(() => {
        if (!isReady) return null;
        return (
            <Fragment>
                {advice && (
                    <Advice
                        type={adviceType}
                        className={classes.advice}
                        showIcon
                        showCollapse={false}
                    >
                        {advice}
                    </Advice>
                )}
                <div className={classes.search}>
                    <Select
                        placeholder={placeholderSearch}
                        options={selectOptions}
                        onChange={onChangeSelectedFilters}
                        value={selectedFilters}
                        isFuzzy
                        isMulti
                        isFullWidth
                        hideMultivalueChips
                    />
                </div>
            </Fragment>
        );
    }, [
        advice,
        adviceType,
        classes.advice,
        classes.search,
        isReady,
        onChangeSelectedFilters,
        placeholderSearch,
        selectOptions,
        selectedFilters,
    ]);

    const modalClasses = useMemo(
        () => ({
            content: classes.content,
            deleteButton: classes.backButton,
        }),

        [classes.content, classes.backButton],
    );

    const overrides = {
        confirmButton: {
            isLoading,
            isDisabled: confirmIsDisabled,
        },
    };

    return (
        <Modal
            className={rootClassNames}
            overlayClassName={overlayClassName || classes.overlay}
            classes={modalClasses}
            title={title}
            isOpen={isOpen}
            size={size}
            useContentStaticHeight={true}
            onRequestClose={onCancel}
            onCancel={onCancel}
            onConfirm={onConfirm}
            onDelete={onBack}
            cancelText={cancelText}
            confirmText={confirmText}
            deleteText={backText}
            preComponent={preComponent}
            postComponent={postComponent}
            overrides={overrides}
        >
            {!isReady && (
                <div className={classes.loader}>
                    <Loader size="massive" />
                </div>
            )}
            {isReady && (
                <div className={classes.optionsContainer}>
                    {!selectedFilters.length && (
                        <div className={classes.emptyContainer}>
                            <FilterOff
                                color={theme.colors.grey[500]}
                                className={classes.emptyIcon}
                            />
                            <Text className={classes.emptyText}>
                                {t('cfm_label_download_data_template_no_filters')}
                            </Text>
                            <Text className={classes.emptyText}>
                                {t('cfm_label_download_data_template_no_filters_2')}
                            </Text>
                        </div>
                    )}
                    {!!selectedFilters.length && (
                        <FiltersProvider>
                            <Filters
                                className={classes.filters}
                                useDrawer={false}
                                schema={selectedFiltesSchema}
                                toggled={openFilters}
                                showAdd={false}
                                onToggle={onToggleFilter}
                                onChange={onChangeFilter}
                                values={filtersValues}
                                useViewsInFilters={false}
                                useHeader={false}
                                useSections={false}
                                isPinnable={false}
                                isClearable={false}
                                isFullWidth
                            />
                        </FiltersProvider>
                    )}
                </div>
            )}
        </Modal>
    );
}

ModalFilters.propTypes = {
    advice: PropTypes.string,
    adviceType: PropTypes.string,
    className: PropTypes.string,
    title: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    backText: PropTypes.string,
    placeholderSearch: PropTypes.string,
    isOpen: PropTypes.bool,
    schema: PropTypes.array,
    selectOptions: PropTypes.array,
    onChange: PropTypes.func,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    onBack: PropTypes.func,
    size: PropTypes.string,
    confirmIsDisabled: PropTypes.bool,
    overlayClassName: PropTypes.string,
    postComponent: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default memo(ModalFilters);
